import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useTranslation } from 'react-i18next';

const Navbar = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  
  // Load header background image on full width
  React.useEffect(() => {
    const img = new Image();
    img.onload = () => {
      console.log('✅ Header background image loaded successfully');
      const navbar = document.getElementById('desktop-nav');
      if (navbar) {
        // Set background image directly without heavy gradient overlay
        navbar.style.background = `linear-gradient(rgba(0,181,172,0.3) 0%, rgba(0,155,146,0.3) 100%), url('/images/header-bg.jpg')`;
        navbar.style.backgroundSize = 'cover';
        navbar.style.backgroundPosition = 'center';
        navbar.style.backgroundRepeat = 'no-repeat';
        navbar.style.width = '100%';
        navbar.classList.add('with-bg-image');
        console.log('✅ Background applied to navbar - full width');
      }
    };
    img.onerror = () => {
      console.log('❌ Header background image not found at /images/header-bg.jpg');
      console.log('🔍 Make sure header-bg.jpg exists in client/public/images/');
    };
    img.src = '/images/header-bg.jpg';
  }, []);

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Profile Avatar Component
  const ProfileAvatar = ({ size = 'small', className = '' }) => {
    const avatarSize = size === 'small' ? 32 : 40;
    
    const getAvatarUrl = () => {
      if (!user?.photoUrl) return null;
      return `http://localhost:8000${user.photoUrl}?t=${Date.now()}`;
    };
    
    return (
      <div className={`profile-avatar ${className}`}>
        {user?.photoUrl ? (
          <img 
            src={getAvatarUrl()} 
            alt="Profile" 
            className="avatar-img"
            style={{ width: avatarSize, height: avatarSize }}
            onError={(e) => {
              console.error('Avatar image failed to load:', user.photoUrl);
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
            onLoad={() => console.log('Avatar image loaded:', user.photoUrl)}
          />
        ) : null}
        <div 
          className="avatar-placeholder"
          style={{ 
            width: avatarSize, 
            height: avatarSize,
            display: user?.photoUrl ? 'none' : 'flex'
          }}
        >
          <svg 
            width={avatarSize * 0.6} 
            height={avatarSize * 0.6} 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        </div>
      </div>
    );
  };

  // SVG Icons
  const HomeIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9,22 9,12 15,12 15,22"/>
    </svg>
  );

  const MembersIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="8.5" cy="7" r="4"/>
      <path d="M20 8v6M23 11h-6"/>
    </svg>
  );

  const GroupIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );

  const FAQIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  );


  return (
    <>
      {/* Desktop Navigation */}
      <nav className="navbar desktop-nav" id="desktop-nav">
        <div className="nav-container">
          <Link to="/" className="nav-logo">
            <img src="/images/logo.png" alt="togee" />
          </Link>
          <div className="nav-links">
            <Link to="/" className="nav-link">
              {t('navigation.events')}
            </Link>
            <Link to="/members" className="nav-link">
              {t('navigation.members')}
            </Link>
            <Link to="/groups" className="nav-link">
              {t('navigation.groups')}
            </Link>
            <Link to="/faq" className="nav-link">
              {t('navigation.faq')}
            </Link>
            <Link to="/profile" className="nav-link">
              {t('navigation.profile')}
            </Link>
            <div className="nav-user">
              <ProfileAvatar size="small" />
              <span>Welcome, {user?.name}</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Footer */}
      <nav className="mobile-nav">
        <div className="mobile-nav-container">
          <Link 
            to="/" 
            className={`mobile-nav-item ${isActive('/') ? 'active' : ''}`}
          >
            <HomeIcon />
            <span>{t('navigation.events')}</span>
          </Link>
          <Link 
            to="/members" 
            className={`mobile-nav-item ${isActive('/members') ? 'active' : ''}`}
          >
            <MembersIcon />
            <span>{t('navigation.members')}</span>
          </Link>
          <Link 
            to="/groups" 
            className={`mobile-nav-item ${isActive('/groups') ? 'active' : ''}`}
          >
            <GroupIcon />
            <span>{t('navigation.groups')}</span>
          </Link>
          <Link 
            to="/faq" 
            className={`mobile-nav-item ${isActive('/faq') ? 'active' : ''}`}
          >
            <FAQIcon />
            <span>{t('navigation.faq')}</span>
          </Link>
          <Link 
            to="/profile" 
            className={`mobile-nav-item ${isActive('/profile') ? 'active' : ''}`}
          >
            <div className="mobile-nav-avatar">
              <ProfileAvatar size="small" />
            </div>
            <span>{t('navigation.profile')}</span>
          </Link>
        </div>
      </nav>
    </>
  );
};

export default Navbar; 